import React, { ReactElement } from 'react';
// mui components
import {
  FormControl,
  FormHelperText,
  Grid,
  OutlinedInput as MuiOutlinedInput,
  outlinedInputClasses,
  OutlinedInputProps,
  Typography,
  Tooltip,
} from '@mui/material';
import AllertIcon from 'assets/icons/alert-icon.svg';
import { styled } from '@mui/material/styles';
import { FormControlProps } from '@mui/material/FormControl/FormControl';
import CustomTooltip from 'components/custom-tooltip';
import { CSSProperties } from '@mui/styles';

export interface IOutlinedInputProps extends OutlinedInputProps {
  name?: string;
  label?: string;
  tooltip?: boolean;
  tooltipIcon?: any;
  tooltipText?: React.ReactNode;
  width?: string;
  required?: boolean;
  helperText?: string;
  placeholder?: string;
  withoutHelperText?: boolean;
  sxControl?: FormControlProps['sx'];
  InputProps?: any;
  className?: string;
  optional?: boolean;
  smallsize?: string;
  secondLabel?: ReactElement;
  unstyled?: boolean;
  unstyledInputStyles?: CSSProperties;
  height?: string;
}

const StyledOutlinedInputNewUI = styled(({ optional: _, ...props }: IOutlinedInputProps) => (
  <MuiOutlinedInput {...props} />
))(({ theme, readOnly, width, multiline, smallsize, height }) => ({
  pointerEvents: readOnly ? 'none' : 'auto',

  [`&.${outlinedInputClasses.root}`]: {
    height: multiline ? '100%' : height ? height : '48px',
    width: width || '100%',
    backgroundColor: readOnly ? '#FAFBFC' : '#fff',
    borderRadius: '12px',
    transition: theme.transitions.create(['border-color', 'background-color', 'box-shadow']),
    '&:before': {
      border: 'unset !important',
    },
    '&:after': {
      border: 'unset !important',
    },
    '&:hover': {
      [`& .${outlinedInputClasses.notchedOutline}`]: {
        border: '1px solid #EDEFF7',
      },
    },
    '&.Mui-focused': {
      [`& .${outlinedInputClasses.notchedOutline}`]: {
        borderColor: '#EDEFF7',
        borderWidth: '1px',
      },
    },
    '&.Mui-error': {
      [`& .${outlinedInputClasses.notchedOutline}`]: {
        borderColor: '#EDEFF7',
      },
    },
    '&.Mui-disabled': {
      [`& .${outlinedInputClasses.notchedOutline}`]: {
        borderColor: '#EDEFF7',
      },
    },
  },

  [`& .${outlinedInputClasses.input}`]: {
    fontSize: '14px',
    lineHeight: '146%',
    fontWeight: 400,
    color: '#23252F',
    fontFamily: 'Inter, sans-serif !important',
  },
  [`& .${outlinedInputClasses.notchedOutline}`]: {
    border: '1px solid #EDEFF7',
    borderRadius: smallsize ? '6px !important' : '12px',
    '&.Mui-error': {
      border: '1px solid #EDEFF7',
    },
  },
  '& .MuiSelect-icon': {
    top: 'calc(50% - 11.5px)',
    right: '14px',
    transition: 'all 0.3s ease-in-out',
  },
  '& input::placeholder': {
    fontSize: '14px',
    lineHeight: '146%',
    color: '#8C8F9D',
    opacity: 1,
    fontFamily: 'Inter, sans-serif !important',
  },
}));

const UnStyledOutlinedInput = styled(({ optional: _, ...props }: IOutlinedInputProps) => (
  <MuiOutlinedInput {...props} />
))(({ theme, readOnly, width, multiline, unstyledInputStyles }) => ({
  pointerEvents: readOnly ? 'none' : 'auto',

  [`&.${outlinedInputClasses.root}`]: {
    height: multiline ? '100%' : '24px',
    width: width || '100%',
    backgroundColor: readOnly ? '#FAFBFC' : '#fff',
    borderRadius: '0px',
    transition: theme.transitions.create(['border-color', 'background-color', 'box-shadow']),
    ...unstyledInputStyles,

    '&:before': {
      border: 'unset !important',
    },
    '&:after': {
      border: 'unset !important',
    },
    '&:hover': {
      [`& .${outlinedInputClasses.notchedOutline}`]: {
        border: '1px solid transparent',
        borderBottom: '1px solid #EDEFF7',
      },
    },
    '&.Mui-focused': {
      [`& .${outlinedInputClasses.notchedOutline}`]: {
        border: '1px solid transparent',
        borderBottom: '1px solid #EDEFF7',
      },
    },
    '&.Mui-error': {
      [`& .${outlinedInputClasses.notchedOutline}`]: {
        border: '1px solid transparent',
        borderBottom: '1px solid red',
      },
    },
    '&.Mui-disabled': {
      [`& .${outlinedInputClasses.notchedOutline}`]: {
        border: '1px solid transparent',
        borderBottom: '1px solid #EDEFF7',
      },
    },
  },

  [`& .${outlinedInputClasses.input}`]: {
    fontSize: '14px',
    lineHeight: '146%',
    fontWeight: 400,
    color: '#23252F',
    fontFamily: 'Inter, sans-serif !important',
    height: multiline ? '100%' : '24px',
  },
  [`& .${outlinedInputClasses.notchedOutline}`]: {
    border: '1px solid transparent',
    borderRadius: '0px',
    '&.Mui-error': {
      border: '1px solid #EDEFF7',
    },
  },
  '& .MuiSelect-icon': {
    top: 'calc(50% - 11.5px)',
    right: '14px',
    transition: 'all 0.3s ease-in-out',
  },
  '& input::placeholder': {
    fontSize: '14px',
    lineHeight: '146%',
    color: '#8C8F9D',
    opacity: 1,
    fontFamily: 'Inter, sans-serif !important',
  },
}));

export const StyledLabel = styled('label')(() => ({
  width: '100%',
  marginBottom: '6px',
  fontSize: '14px',
  lineHeight: '20px',
  fontWeight: 500,
  color: '#1D2939',
}));

export const StyledLabelNewUI = styled('label')(() => ({
  width: '100%',
  marginBottom: '8px',
  fontSize: '14px',
  lineHeight: '146%',
  fontWeight: 400,
  color: '#23252F',
  fontFamily: 'Inter, sans-serif !important',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
}));

const OutlinedInput: React.FC<IOutlinedInputProps> = ({
  name,
  label,
  placeholder,
  required,
  helperText,
  fullWidth,
  readOnly,
  withoutHelperText,
  sxControl,
  className,
  optional,
  secondLabel,
  tooltip,
  tooltipIcon,
  tooltipText,
  unstyledInputStyles,
  unstyled,
  ...props
}) => {
  return (
    <FormControl fullWidth={fullWidth} sx={sxControl}>
      {!!label && (
        <StyledLabelNewUI htmlFor={name}>
          <Grid
            sx={{
              width: '100%',
              display: 'flex',
              justifyContent: 'flex-start',
              alignItems: 'center',
            }}
          >
            {label}

            {tooltip && (
              <CustomTooltip title={tooltipText} placement="right" lightMode>
                <AllertIcon />
              </CustomTooltip>
            )}
          </Grid>

          {secondLabel ? (
            secondLabel
          ) : optional ? (
            <Typography
              sx={{
                fontSize: '12px',
                fontWeight: '400',
                lineHeight: '150%',
                color: '#6E707A',
                fontFamily: 'Inter, sans-serif !important',
              }}
            >
              optional
            </Typography>
          ) : null}
        </StyledLabelNewUI>
      )}
      {unstyled ? (
        <UnStyledOutlinedInput
          id={name}
          optional={optional}
          placeholder={placeholder}
          readOnly={readOnly}
          unstyledInputStyles={unstyledInputStyles}
          autoComplete="off"
          {...props}
        />
      ) : (
        <StyledOutlinedInputNewUI
          id={name}
          optional={optional}
          placeholder={placeholder}
          readOnly={readOnly}
          autoComplete="off"
          {...props}
        />
      )}
      {!withoutHelperText && helperText && helperText != ' ' && (
        <FormHelperText sx={{ margin: '3px 0', color: 'red' }}>{helperText || ' '}</FormHelperText>
      )}
    </FormControl>
  );
};

export default OutlinedInput;
